import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import moment from 'moment';
import { toast } from 'react-toastify';
import { inboxOutboxAPI } from 'utils/api/fulcrum-api';
import { removeEmptyFromObj, sleep } from 'utils/functions';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import CustomAdvanceTablePagination from 'components/common/advance-table/CustomAdvanceTablePagination';
import UploadCorrespondence from './UploadCorrespondence';
import InboxTableHeader from './InboxTableHeader';
import UnknownMessageTool from './UnknownMessageTool';
import InboxOutboxFilters from '../InboxOutboxFilters';
import InboxOutboxDetails from '../InboxOutboxDetails';
import ReactInterval from 'react-interval';

const defaultInboxColumns = [
	{
		accessor: 'sender',
		Header: 'Sender'
	},
	{
		accessor: 'mode_of_transmission',
		Header: 'Mode of Transmission'
	},
	{
		accessor: 'claimant',
		Header: 'Claimant'
	},
	{
		accessor: 'createdAt',
		Header: 'Received'
	},
	{
		accessor: 'actions',
		Header: 'Actions'
	}
];

const InboxTable = ({ columns = defaultInboxColumns, defaultSearchParams = {}, requiredSearchParams = { direction: "Inbound" }, refreshParent = () => { } } = {}) => {
	const [inboxData, setInboxData] = useState([]);
	const [Pages, setPages] = useState([1]);
	const [activePage, setActivePage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [inboxDataCount, setInboxDataCount] = useState(0);
	const [uploadCorrespondenceModalState, setUploadCorrespondenceModalState] = useState(false);
	const [searching, setSearching] = useState(false);
	const [lastQuery, setLastQuery] = useState({});
	const [searchParams, setSearchParams] = useState({ ...defaultSearchParams, ...requiredSearchParams, ...{ direction: "Inbound" } });
	let defaultSort = ["created_at", "DESC"];
	const [appliedSortOrders, setAppliedSortOrders] = useState([defaultSort]);
	const [uknownMessageToolModalState, setUknownMessageToolModalState] = useState(false);
	const [selectedCorrespondence, setSelectedCorrespondence] = useState({});
	const [inboxOutboxDetailsModalState, setInboxOutboxDetailsModalState] = useState(false);

	const deleteInboxItem = async (accountId, id) => {
		if (!id) return;

		let confirmed = await Swal.fire({
			title: 'Permanently delete inbox item?',
			text: "You won't be able to revert this!",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: "Yes, delete it!"
		}).then((result) => (result.isConfirmed));
		if (!confirmed) {
			return;
		}

		try {
			let { error, response, message = '' } = await inboxOutboxAPI.deleteInboxOutbox(accountId, id);
			if (error) {
				if (typeof response?.error === 'string') {
					throw new Error(`${error} - ${response.error}`);
				} else if (typeof response === 'string') {
					throw new Error(`${error} - ${response}`);
				}

				let { statusCode } = response?.error || response || {};
				if (!message) {
					message = `${error} - There was an error deleting.`;
				}
				if (statusCode) {
					message = `${statusCode} - ${message}`;
				} else {
					message = `${error} - ${message}`;
				}
				throw new Error(message);
			}

			refreshLastQuery();
			toast.success('Successfully deleted inbox item', {
				theme: 'colored'
			});
		} catch (err) {
			console.log(err);
			let error_message = err?.message || err?.data?.error?.message || err || "There was an error deleting inbox item. See console for error";
			console.log(error_message);
			toast.error(error_message, {
				theme: 'colored'
			});
		}
	};

	const handleBulkActions = (actionObj = {}, selectedRows = [], selectedIds = []) => {
		let inboxItem = {};
		if (Array.isArray(selectedRows) && selectedRows.length === 1) {
			inboxItem = selectedRows[0];
		}
		let { bulk_action } = actionObj || {};
		switch (bulk_action) {
			case 'delete':
				// (inboxItem.id) && deleteInboxItem(inboxItem.id);
				break;
			case 'Assign to Claimant':
				alert("Assign to Claimant");
				break;
			case 'Unassociate Claimant':
				alert("Unassociate Claimant");
				break;
			default:
				alert('Bulk Action not handled', bulk_action)
				console.log("@todo handleBulkActions", actionObj, "selectedRows", selectedRows, "selectedIds", selectedIds, "inboxItem", inboxItem);
				break;
		}
	};

	const formatData = (data = []) => {
		let adjustedData = data.map(item => {
			return {
				...item,
				createdAt: moment(item.createdAt).format('MM/DD/YYYY hh:mm A'),
				updatedAt: moment(item.updatedAt).format('MM/DD/YYYY hh:mm A'),
				claimant: item?.claimant ? <Link to={`/claimant/profile/${item.claimant.id}`}>{item.claimant.first_name} {item.claimant.last_name}</Link> : '',
				actions: (<Flex className="justify-content-end">
					<CardDropdown>
						<Dropdown.Item onClick={(e) => viewInboxItem(item)} >
							<span>View</span>
						</Dropdown.Item>
						{
							item?.claimant_id
								?
								<Dropdown.Item onClick={() => { handleUnassociateClaimant(item) }} >
									<span>Unassociate Claimant</span>
								</Dropdown.Item>
								:
								<Dropdown.Item onClick={(e) => { handleAssignClaimant(item) }} >
									<span>Assign to Claimant</span>
								</Dropdown.Item>
						}
						<Dropdown.Divider />
						<Dropdown.Item onClick={() => deleteInboxItem(item?.account_id || null, item.id)} >
							<span>Delete</span>
						</Dropdown.Item>
					</CardDropdown>
				</Flex>)
			}
		});
		setInboxData(adjustedData);
	};

	// Clear search params and reset to default
	const clearSearchParams = () => {
		if (JSON.stringify(searchParams) !== JSON.stringify({ ...defaultSearchParams, ...requiredSearchParams })) {
			setSearchParams({ ...defaultSearchParams, ...requiredSearchParams });
		}
		if (JSON.stringify(appliedSortOrders) !== JSON.stringify([defaultSort])) {
			setAppliedSortOrders([defaultSort]);
		}
		if (activePage !== 1) {
			setActivePage(1);
		}
		if (perPage !== 10) {
			setPerPage(10);
		}
	};

	// Show unassigned inbox items
	const showUnassigned = () => {
		setSearchParams({ ...searchParams, claimant: 'Unassigned' });
	};

	// View inbox item
	const viewInboxItem = (inboxItem = {}) => {
		let { id } = inboxItem || {};
		if (!id) {
			setSelectedCorrespondence({});
		} else {
			setSelectedCorrespondence(inboxItem);
			setInboxOutboxDetailsModalState(true);
		}
	};

	// Handle claimant unnasociation
	const handleUnassociateClaimant = async (inboxItem = {}) => {
		let { id, claimant_id, account_id: accountId } = inboxItem || {};
		if (!id || !claimant_id) {
			alert("Missing inbox item or claimant id.");
			return;
		}
		Swal.fire({
			title: 'Unassociate Claimant?',
			text: "Are you sure you would like to unassociate this claimant from this correspondence?",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: "Yes, unassociate it!"
		}).then(async (result) => {
			if (result.isConfirmed) {
				try {
					let result = await inboxOutboxAPI.updateInboxOutbox(accountId, id, { claimant_id: null });
					if (!result?.data) {
						throw new Error("Unable to unassociate claimant from correspondence.");
					}

					refreshLastQuery();
					toast.success("Successfully unassociated claimant from correspondence.", { theme: 'colored' });
				} catch (err) {
					toast.error("Unable to unnasociate the claimant from the inbox item.", { theme: 'colored' });
				}
			}
		});
	}

	// Handle claimant assign a claimant click
	const handleAssignClaimant = async (inboxItem = {}) => {
		let { id } = inboxItem || {};
		if (!id) {
			alert('Unable to assign claimant to correspondence. Missing inbox item id.')
			return;
		}
		setSelectedCorrespondence(inboxItem);
		await sleep(100);
		setUknownMessageToolModalState(true);
	}

	const headerClickFn = (id, previousSortBy) => {
		if (!id) {
			return;
		}
		let foundIndex = appliedSortOrders.findIndex(s => s[0] === id);
		let sortBy = !previousSortBy ? "ASC" : previousSortBy === "asc" ? "DESC" : '';

		if (!sortBy) { // Not Sorted so remove clicked sort item 
			if (foundIndex > -1) {
				setAppliedSortOrders((prev) => {
					let newSortOrders = [...prev];
					newSortOrders.splice(foundIndex, 1); // remove clicked sort item
					return newSortOrders;
				});
			}
		} else { // Sort by Ascending or Descending 
			let newSort = [id, sortBy]
			if (foundIndex > -1) { // replaces existing 
				setAppliedSortOrders((prev) => {
					let newSortOrders = [...prev];
					newSortOrders[foundIndex] = newSort;
					return newSortOrders;
				});
			} else { // inserts 
				setAppliedSortOrders((prev) => [...prev, newSort]);
			}
		}
	};

	const getInboxData = async (params = {}, saveLastQuery = true, options = {}) => {
		setSearching(true);
		try {
			params = removeEmptyFromObj(params);
			params.page = activePage;
			params.limit = perPage;
			params.navSize = 5; // How many buttons to show for navigation 
			params.order = appliedSortOrders; // Example [[id, 'ASC']]
			let { error, response, data } = await inboxOutboxAPI.searchInboxOutboxes(params, options);
			if (error) {
				if (typeof response?.error === 'string') {
					throw new Error(`${error} - ${response.error}`);
				} else if (typeof response === 'string') {
					throw new Error(`${error} - ${response}`);
				}
				throw new Error(`${error} - There was an error searching inbox.`);
			}

			let { count = 0, rows = [], pages = [], page = 1, pageCount = 0 } = data || {}; // data = { count = 0, rows = [], limit, pageCount, page, pages = [1,2,3,10] } 
			if (!rows || (Array.isArray(rows) && rows.length === 0)) {
				throw new Error('No inbox data found');
			}

			if (pageCount < activePage) {
				setActivePage(1);
			} else {
				setActivePage(page);
			}
			setPages(pages);
			formatData(rows);
			setInboxDataCount(count);

			if (saveLastQuery) {
				setLastQuery(params);
			}
		} catch (error) {
			console.log("Error getting inbox data", error);
			setInboxData([]);
			setInboxDataCount(0);
			setActivePage(1);
		}
		setSearching(false);
	};

	const refreshLastQuery = async (options = {}) => {
		await getInboxData({ ...lastQuery, ...requiredSearchParams }, false, options);
		refreshParent();
	};

	useEffect(() => {
		getInboxData(searchParams);
	}, [searchParams, activePage, perPage, appliedSortOrders]);

	return (
		<AdvanceTableWrapper
			columns={columns}
			data={inboxData}
			// selection
			sortable
			pagination
			perPage={perPage}
			setPerPage={setPerPage}
		>
			<Card className="mb-3 shadow-none">
				<Card.Header>
					<InboxTableHeader
						table
						searchParams={searchParams}
						setUploadCorrespondenceModalState={setUploadCorrespondenceModalState}
						handleBulkActions={handleBulkActions}
						showUnassigned={showUnassigned}
					/>
					<InboxOutboxFilters
						searchParams={searchParams}
						setSearchParams={setSearchParams}
						clearSearchParams={clearSearchParams}
						fields={[
							"sender",
							"mode_of_transmission",
							// "recipient",
							"claimant",
							"recieved_start_date",
							"recieved_end_date",
							// "status",
						]}
					/>
				</Card.Header>
				<Card.Body className="p-0">
					{
						searching ?
							<p>Loading Inbox...</p>
							:
							<CustomAdvanceTable
								table
								headerClickFn={headerClickFn}
								appliedSortOrders={appliedSortOrders}
								headerClassName="bg-200 text-900 text-nowrap align-middle"
								rowClassName="align-middle white-space-nowrap"
								tableProps={{
									bordered: true,
									striped: true,
									className: 'fs--1 mb-0'
								}}
							/>
					}

				</Card.Body>
				<Card.Footer>
					<CustomAdvanceTablePagination
						table
						Pages={Pages}
						activePage={activePage}
						setActivePage={setActivePage}
						totalRecords={inboxDataCount}
					/>
				</Card.Footer>
			</Card>

			<UploadCorrespondence
				modalState={uploadCorrespondenceModalState}
				setModalState={setUploadCorrespondenceModalState}
				refreshParent={refreshLastQuery}
			/>

			<UnknownMessageTool
				modalState={uknownMessageToolModalState}
				setModalState={setUknownMessageToolModalState}
				refreshParent={refreshLastQuery}
				selectedCorrespondence={selectedCorrespondence}
				setSelectedCorrespondence={setSelectedCorrespondence}
			/>

			<InboxOutboxDetails
				modalState={inboxOutboxDetailsModalState}
				setModalState={setInboxOutboxDetailsModalState}
				refreshParent={refreshLastQuery}
				selectedCorrespondence={selectedCorrespondence}
				setSelectedCorrespondence={setSelectedCorrespondence}
			/>

			<ReactInterval timeout={15000} enabled={true} callback={() => { refreshLastQuery({ ignoreTouch: true }) }} />

		</AdvanceTableWrapper>
	)
};

InboxTable.propTypes = {
	columns: PropTypes.array,
	defaultSearchParams: PropTypes.object,
	requiredSearchParams: PropTypes.object,
	refreshParent: PropTypes.func,
};

export default InboxTable;